import { useState } from "react";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { fmtMoney } from "./kitchenUtils";

const cls = "h-9 rounded-lg bg-white border border-black/10 px-2 text-[12.5px]";

const LineRow = ({ ln, row, ings, onChange }) => {
  const ing = ings.find((i) => i.id === row.ingredient_id);
  const changed = ing?.pack_cost != null && row.pack_cost !== "" && Math.abs(Number(row.pack_cost) - ing.pack_cost) >= 0.01;
  return (
    <tr data-testid={`vinv-line-${row.index}`} className="border-t border-black/5 align-top">
      <td className="py-2 pr-2">
        <p className="font-bold text-[13px] text-brand-charcoal">{ln.description}</p>
        <p className="text-[11px] text-brand-charcoal/50">{ln.item_code && `#${ln.item_code} · `}{ln.quantity} {ln.unit}{ln.pack_size && ` · ${ln.pack_size}`} · {fmtMoney(ln.line_total)}</p>
      </td>
      <td className="py-2 pr-2">
        <select value={row.ingredient_id} onChange={(e) => onChange({ ingredient_id: e.target.value, pack_qty: ings.find((i) => i.id === e.target.value)?.pack_qty ?? "" })} data-testid={`vinv-ingredient-${row.index}`} className={`${cls} w-full max-w-[200px]`}>
          <option value="">Skip this line</option>
          {ings.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        {ing && <p className="mt-1 text-[11px] text-brand-charcoal/50">Now {fmtMoney(ing.pack_cost)} per {ing.pack_label || "pack"}</p>}
      </td>
      <td className="py-2 pr-2">
        <input type="number" min="0" step="0.01" value={row.pack_cost} onChange={(e) => onChange({ pack_cost: e.target.value })} disabled={!ing} data-testid={`vinv-cost-${row.index}`} className={`${cls} w-24`} />
        {changed && (
          <p data-testid={`vinv-change-${row.index}`} className={`mt-1 text-[11px] font-bold ${Number(row.pack_cost) > ing.pack_cost ? "text-red-600" : "text-brand-teal"}`}>
            {Number(row.pack_cost) > ing.pack_cost ? "Price up" : "Price down"}
          </p>
        )}
      </td>
      <td className="py-2">
        <input type="number" min="0" step="any" value={row.pack_qty} onChange={(e) => onChange({ pack_qty: e.target.value })} disabled={!ing} placeholder="per pack" data-testid={`vinv-qty-${row.index}`} className={`${cls} w-20`} />
        {ing && <p className="mt-1 text-[11px] text-brand-charcoal/50">{ing.unit} / pack</p>}
      </td>
    </tr>
  );
};

// Review AI-read lines, match each to an ingredient, then apply costs.
export const InvoiceReview = ({ invoice, ings, onBack, onApplied }) => {
  const [vendor, setVendor] = useState(invoice.vendor || "");
  const [rows, setRows] = useState(invoice.lines.map((ln, index) => {
    const ing = ings.find((i) => i.id === ln.suggested_ingredient_id);
    return { index, ingredient_id: ing?.id || "", pack_cost: ln.unit_price || "", pack_qty: ing?.pack_qty ?? "" };
  }));
  const [busy, setBusy] = useState(false);
  const applied = invoice.status === "applied";
  const update = (i) => (patch) => setRows((rs) => rs.map((r) => (r.index === i ? { ...r, ...patch } : r)));

  const apply = async () => {
    setBusy(true);
    try {
      const lines = rows.filter((r) => r.ingredient_id && r.pack_cost !== "").map((r) => ({
        index: r.index, ingredient_id: r.ingredient_id, pack_cost: Number(r.pack_cost), pack_qty: r.pack_qty === "" ? null : Number(r.pack_qty),
      }));
      const { data } = await api.post(`/admin/kitchen/vendor-invoices/${invoice.id}/apply`, { vendor, invoice_date: invoice.invoice_date, lines });
      toast.success(`Updated ${data.applied} ingredient cost${data.applied === 1 ? "" : "s"}${data.price_changes.length ? ` · ${data.price_changes.length} price change${data.price_changes.length === 1 ? "" : "s"}` : ""}`);
      onApplied();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't apply invoice.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vinv-review" className="rounded-2xl bg-white border border-black/10 p-4 space-y-3">
      <button type="button" onClick={onBack} data-testid="vinv-back" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60"><ArrowLeft className="h-3.5 w-3.5" /> All invoices</button>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60">Vendor
          <input value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="vinv-vendor" className={`${cls} mt-1 block w-64 normal-case font-normal tracking-normal`} />
        </label>
        <p className="text-[12.5px] text-brand-charcoal/60">#{invoice.invoice_number || "—"} · {invoice.invoice_date || "no date"}</p>
        <a href="#view" onClick={async (e) => { e.preventDefault(); const w = window.open("", "_blank"); const r = await api.get(`/admin/kitchen/vendor-invoices/${invoice.id}/file`, { responseType: "blob" }); if (w) w.location.href = URL.createObjectURL(r.data); }} data-testid="vinv-view-file" className="text-[12px] font-bold text-brand-teal underline">View original</a>
      </div>
      {applied && <p className="flex items-center gap-1.5 text-[12.5px] font-bold text-brand-teal"><CheckCircle2 className="h-4 w-4" /> Applied {invoice.applied_count} line(s). You can re-apply after changes.</p>}
      {invoice.lines.length === 0 ? (
        <p data-testid="vinv-no-lines" className="text-[13px] text-brand-charcoal/60">We couldn&apos;t read any product lines from this file. Try a clearer photo or the PDF.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[10.5px] uppercase tracking-[0.1em] text-brand-charcoal/50"><th className="pb-1">Invoice line</th><th className="pb-1">Ingredient</th><th className="pb-1">Pack cost</th><th className="pb-1">Pack size</th></tr></thead>
            <tbody>{invoice.lines.map((ln, i) => <LineRow key={i} ln={ln} row={rows[i]} ings={ings} onChange={update(i)} />)}</tbody>
          </table>
        </div>
      )}
      <button type="button" onClick={apply} disabled={busy || !rows.some((r) => r.ingredient_id)} data-testid="vinv-apply" className="w-full h-11 rounded-full bg-brand-charcoal text-white text-[12px] font-bold uppercase tracking-[0.1em] disabled:opacity-40 inline-flex items-center justify-center gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update ingredient costs
      </button>
    </div>
  );
};
